/* Public political events — the global feed (calendar PRD §5.5 P2.3/O7):
   GET /public-data/events serves admin-curated statewide events plus events
   organizations published to 'public'. Interest-relevant events float via the
   same topic vocabulary the legislation feed ranks on; nothing is hidden.

   Split of concerns: the DATE WINDOW and LOCATION (ZIP → district ids, county)
   filter server-side — location is a deliberate, infrequent action and the
   district join lives in SQL. Topic / type / party / search filter CLIENT-side
   off that one fetch, so every checkbox is instant and the facet counts are
   honest (they describe the set the user is actually looking at). The server
   also accepts those facets, for the marketing site and for scale. */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';
import { partyBucketOf, type PartyBucketKey } from '../lib/eventVocab';

export interface PublicEvent {
  id: string;
  event_key: string;
  title: string;
  description: string | null;
  event_type: string | null;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  format: string;
  venue: string | null;
  address: string | null;
  city: string | null;
  county: string | null;
  topics: string[];
  host_name: string | null;
  host_party: string | null;
  link_url: string | null;
  registration_url: string | null;
  source_name: string | null;
  source_url: string | null;
  organization_name: string | null;
  attribution: { text: string; url: string | null } | null;
}

export interface EventFilters {
  topics: string[];
  types: string[];
  parties: PartyBucketKey[];
  search: string;
}

export const EMPTY_EVENT_FILTERS: EventFilters = { topics: [], types: [], parties: [], search: '' };

export interface EventLocation {
  label: string;
  districtIds: string[];
  county: string | null;
}

/** Shared-address districts → the shape the feed filters on. */
export function eventLocationFrom(
  districts: { formatted_address?: string; address?: string; congressional_district_id?: string | null; state_senate_district_id?: string | null; state_house_district_id?: string | null; labels?: Record<string, string> } | null,
): EventLocation | null {
  if (!districts) return null;
  const districtIds = [
    districts.congressional_district_id,
    districts.state_senate_district_id,
    districts.state_house_district_id,
  ].filter(Boolean) as string[];
  if (!districtIds.length && !districts.labels?.county) return null;
  return {
    label: districts.formatted_address || districts.address || 'Your address',
    districtIds,
    county: districts.labels?.county ?? null,
  };
}

interface Options {
  /** Interest topics used only for ordering, never for hiding. */
  interests?: string[];
  filters?: EventFilters;
  location?: EventLocation | null;
  /** Days ahead to fetch. */
  days?: number;
}

const countBy = <T extends string>(rows: PublicEvent[], key: (e: PublicEvent) => T | T[] | null) => {
  const counts: Record<string, number> = {};
  for (const row of rows) {
    const value = key(row);
    const values = value == null ? [] : Array.isArray(value) ? value : [value];
    for (const v of values) counts[v] = (counts[v] || 0) + 1;
  }
  return counts;
};

export function usePublicEvents({
  interests = [],
  filters = EMPTY_EVENT_FILTERS,
  location = null,
  days = 30,
}: Options = {}) {
  const districtKey = location?.districtIds.join(',') || '';
  const query = useQuery({
    queryKey: ['citizen', 'public-events', districtKey, location?.county ?? '', days],
    queryFn: async (): Promise<{ events: PublicEvent[]; total: number }> => {
      const end = new Date();
      end.setDate(end.getDate() + days);
      const response = await api.get('/public-data/events', {
        params: {
          end_date: end.toISOString().slice(0, 10),
          limit: 500,
          ...(location?.districtIds.length ? { district_id: location.districtIds } : {}),
          ...(location?.county ? { county: location.county } : {}),
        },
      });
      const data = response.data?.data ?? {};
      return { events: data.events ?? [], total: data.total ?? (data.events?.length ?? 0) };
    },
    staleTime: 5 * 60 * 1000,
  });

  const all = useMemo(() => query.data?.events ?? [], [query.data]);

  /** Counts describe the set before that facet is applied, so a zero is visible. */
  const facets = useMemo(
    () => ({
      types: countBy(all, (e) => (e.event_type ? (e.event_type as string) : null)),
      parties: countBy(all, (e) => partyBucketOf(e.host_party)),
      topics: countBy(all, (e) => e.topics ?? []),
    }),
    [all]
  );

  const events = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const filtered = all.filter((event) => {
      if (filters.types.length && !filters.types.includes(event.event_type || '')) return false;
      if (filters.parties.length) {
        const bucket = partyBucketOf(event.host_party);
        if (!bucket || !filters.parties.includes(bucket)) return false;
      }
      if (filters.topics.length && !event.topics?.some((t) => filters.topics.includes(t))) return false;
      if (search) {
        const haystack = [event.title, event.description, event.host_name, event.venue, event.city]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
    if (interests.length === 0) return filtered;
    const matches = (event: PublicEvent) => event.topics?.some((topic) => interests.includes(topic));
    return [...filtered.filter(matches), ...filtered.filter((e) => !matches(e))];
  }, [all, filters, interests]);

  return {
    ...query,
    /** Filtered + interest-ordered. */
    events,
    /** Everything in the window for this location — the facet-count basis. */
    allEvents: all,
    facets,
    total: query.data?.total ?? all.length,
  };
}
